// Ordenamiento de la tabla de clientes por coach. Lógica pura: recibe la lista
// y devuelve una copia ordenada, sin tocar el original ni saber nada de React.

export const COLUMNAS_ORDENABLES = [
  { clave: 'cliente', etiqueta: 'Cliente' },
  { clave: 'plan', etiqueta: 'Plan' },
  { clave: 'monto', etiqueta: 'Monto' },
  { clave: 'meses', etiqueta: 'Antigüedad' },
  { clave: 'comision', etiqueta: 'Comisión' },
  { clave: 'bruto', etiqueta: 'Bruto mes' },
  { clave: 'liquido', etiqueta: 'Líquido mes' },
  { clave: 'estado', etiqueta: 'Estado' },
  { clave: 'proximoPago', etiqueta: 'Próximo pago' },
  { clave: 'ultimoCobro', etiqueta: 'Último cobro' },
  { clave: 'ultimoPago', etiqueta: 'Último pago' },
];

const valorDe = (c, columna) => {
  switch (columna) {
    case 'cliente': return c.nombre || '';
    case 'plan': return c.planId || '';
    // El monto de un cliente sin suscripción es 0, y 0 es un valor, no un
    // hueco: ordena como cero y no se va al final.
    case 'monto': return Number(c.monto) || 0;
    // Sin reparto (clientes de Agustín, sin comisión) es hueco y va al final.
    case 'meses': return c.reparto ? Number(c.reparto.mesesPagados) || 0 : '';
    case 'comision': return c.reparto ? Number(c.reparto.comision) || 0 : '';
    case 'bruto': return c.reparto ? Number(c.reparto.bruto) || 0 : '';
    case 'liquido': return c.reparto ? Number(c.reparto.liquido) || 0 : '';
    case 'ultimoCobro': return c.ultimoCobro || '';
    case 'ultimoPago': return c.ultimoPago || '';
    case 'proximoPago': return c.proximoPago || '';
    default: return '';
  }
};

const esVacio = (v) => v === '' || v === null || v === undefined;

/**
 * Compara por estado: el criterio varía según la dirección (morosos primero en asc,
 * al día primero en desc), pero el desempate entre morosos por diasAtraso es SIEMPRE
 * de mayor a menor. Esto tiene sentido porque estado es una categoría, no un valor
 * continuo: lo que importa es saber a quién cobrarle primero, y eso no cambia porque
 * toques la flecha de ordenamiento.
 */
const compararEstado = (a, b, signo) => {
  const ma = a.estado === 'moroso';
  const mb = b.estado === 'moroso';
  if (ma !== mb) return (ma ? -1 : 1) * signo;
  if (ma && mb) return (Number(b.diasAtraso) || 0) - (Number(a.diasAtraso) || 0);
  return 0;
};

/**
 * Devuelve una COPIA ordenada. Nunca muta la lista que recibe, porque el
 * llamador la tiene en el estado de React y mutarla se ve como "a veces no se
 * actualiza", que es de los bugs más caros de encontrar.
 *
 * Las filas sin dato en la columna elegida quedan SIEMPRE al final, en las dos
 * direcciones: al invertir, lo que se invierte es el orden de las que sí tienen
 * valor.
 */
export const ordenarClientes = (clientes = [], columna = null, dir = 'asc') => {
  const copia = [...clientes];
  if (!columna) return copia;

  const signo = dir === 'desc' ? -1 : 1;

  return copia.sort((a, b) => {
    if (columna === 'estado') return compararEstado(a, b, signo);

    const va = valorDe(a, columna);
    const vb = valorDe(b, columna);

    const aVacio = esVacio(va);
    const bVacio = esVacio(vb);
    if (aVacio && bVacio) return 0;
    if (aVacio) return 1;   // sin signo: el vacío va al final siempre
    if (bVacio) return -1;

    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * signo;
    return String(va).localeCompare(String(vb), 'es') * signo;
  });
};
